import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  SiWordpress, SiShopify, SiWebflow, SiWix, SiSquarespace, SiJoomla,
  SiDrupal, SiWoocommerce, SiBigcommerce, SiTumblr, SiBlogger, SiGoogletagmanager,
  SiFramer, SiElementor, SiGhost, SiHubspot,
} from 'react-icons/si';
import { FaCode } from 'react-icons/fa';
import AIChatWidget from '../components/AIChatWidget';

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  .lp-root {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    background: #ffffff;
    color: #0f172a;
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
  }

  @keyframes fadeUp   { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
  @keyframes shimmer  { 0% { background-position:-200% center; } 100% { background-position:200% center; } }
  @keyframes pulse    { 0%,100% { opacity:1; } 50% { opacity:.5; } }
  @keyframes ticker   { 0% { transform:translateX(0); } 100% { transform:translateX(-50%); } }
  @keyframes slideIn  { from { opacity:0; transform:translateX(100%); } to { opacity:1; transform:translateX(0); } }

  .lp-fadeup   { animation: fadeUp .65s ease both; }
  .lp-delay1   { animation-delay:.1s; }
  .lp-delay2   { animation-delay:.2s; }
  .lp-delay3   { animation-delay:.3s; }

  .lp-gradient-text {
    background: linear-gradient(135deg, #2563eb 0%, #7c3aed 50%, #059669 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: shimmer 5s linear infinite;
  }

  /* Nav */
  .lp-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
    background: rgba(255,255,255,0.95);
    backdrop-filter: blur(16px);
    border-bottom: 1px solid #f1f5f9;
    transition: box-shadow .3s;
  }
  .lp-nav.scrolled { box-shadow: 0 1px 20px rgba(0,0,0,0.08); }
  .lp-nav-inner {
    max-width: 1200px; margin: 0 auto;
    display: flex; align-items: center; height: 64px;
    padding: 0 24px; gap: 8px;
  }
  .lp-nav-links { display: flex; gap: 4px; margin-left: 32px; }
  .lp-nav-a {
    color: #475569; text-decoration: none; font-size: 14px;
    font-weight: 500; padding: 6px 12px; border-radius: 8px;
    transition: color .2s, background .2s;
  }
  .lp-nav-a:hover { color: #0f172a; background: #f1f5f9; }
  .lp-nav-a.active { color: #2563eb; }
  .lp-nav-right { margin-left: auto; display: flex; align-items: center; gap: 8px; }
  .lp-hamburger {
    display: none; flex-direction: column; gap: 5px; cursor: pointer;
    background: none; border: none; padding: 8px; border-radius: 8px;
    transition: background .2s;
  }
  .lp-hamburger:hover { background: #f1f5f9; }
  .lp-hamburger span { width: 22px; height: 2px; background: #475569; border-radius: 2px; display: block; transition: all .3s; }

  /* Mobile menu */
  .lp-mobile-menu {
    display: none; position: fixed; inset: 0; z-index: 999;
    background: #fff; padding: 80px 24px 32px;
    flex-direction: column; gap: 8px;
    animation: slideIn .25s ease;
  }
  .lp-mobile-menu.open { display: flex; }
  .lp-mobile-a {
    color: #0f172a; text-decoration: none; font-size: 18px;
    font-weight: 600; padding: 14px 0;
    border-bottom: 1px solid #f1f5f9;
    display: block;
  }

  /* Buttons */
  .lp-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 6px;
    padding: 10px 20px; border-radius: 10px;
    font-size: 14px; font-weight: 600; text-decoration: none;
    transition: all .2s; cursor: pointer; border: none;
    font-family: 'Inter', sans-serif; white-space: nowrap;
  }
  .lp-btn-blue {
    background: #2563eb; color: #fff;
    box-shadow: 0 2px 8px rgba(37,99,235,.3);
  }
  .lp-btn-blue:hover { background: #1d4ed8; box-shadow: 0 4px 16px rgba(37,99,235,.4); transform: translateY(-1px); }
  .lp-btn-blue-lg {
    background: #2563eb; color: #fff; font-size: 16px;
    padding: 14px 32px; border-radius: 12px;
    box-shadow: 0 4px 20px rgba(37,99,235,.35);
  }
  .lp-btn-blue-lg:hover { background: #1d4ed8; box-shadow: 0 8px 32px rgba(37,99,235,.45); transform: translateY(-2px); }
  .lp-btn-outline {
    background: transparent; color: #374151;
    border: 1.5px solid #e2e8f0; font-size: 16px;
    padding: 13px 32px; border-radius: 12px;
  }
  .lp-btn-outline:hover { border-color: #2563eb; color: #2563eb; background: #eff6ff; }

  /* Cards */
  .lp-card {
    background: #fff; border: 1px solid #e2e8f0;
    border-radius: 16px; transition: all .25s;
  }
  .lp-card:hover { transform: translateY(-4px); box-shadow: 0 16px 48px rgba(0,0,0,0.1); border-color: #bfdbfe; }

  /* Section label */
  .lp-label {
    display: inline-flex; align-items: center; gap: 6px;
    background: #eff6ff; color: #2563eb;
    font-size: 12px; font-weight: 700;
    letter-spacing: 1.2px; text-transform: uppercase;
    padding: 5px 14px; border-radius: 40px;
    border: 1px solid #bfdbfe;
  }

  /* Resources dropdown */
  @keyframes dropDown { from { opacity:0; transform:translateY(-8px) translateX(-50%); } to { opacity:1; transform:translateY(0) translateX(-50%); } }
  .lp-dropdown { position: relative; }
  .lp-dropdown-btn {
    display: flex; align-items: center; gap: 4px;
    color: #475569; font-size: 14px; font-weight: 500;
    padding: 6px 12px; border-radius: 8px; cursor: pointer;
    background: none; border: none; font-family: 'Inter', sans-serif;
    transition: color .2s, background .2s;
  }
  .lp-dropdown-btn:hover, .lp-dropdown-btn.open { color: #0f172a; background: #f1f5f9; }
  .lp-dropdown-arrow { font-size: 10px; transition: transform .2s; display: inline-block; }
  .lp-dropdown-arrow.open { transform: rotate(180deg); }
  .lp-dropdown-menu {
    position: absolute; top: 100%; left: 50%;
    margin-top: 10px;
    transform: translateX(-50%);
    background: #fff; border: 1px solid #e2e8f0; border-radius: 16px;
    padding: 8px; box-shadow: 0 16px 48px rgba(0,0,0,0.12);
    width: 560px; display: grid; grid-template-columns: 1fr 1fr; gap: 2px;
    animation: dropDown .15s ease; z-index: 400;
  }
  /* Invisible bridge so the cursor can cross the gap without triggering mouseleave */
  .lp-dropdown-menu::before { content: ''; position: absolute; left: 0; right: 0; top: -12px; height: 12px; }
  .lp-dropdown-item {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 12px 14px; border-radius: 10px; text-decoration: none;
    transition: background .15s;
  }
  .lp-dropdown-item:hover { background: #f8fafc; }
  .lp-dropdown-icon { width: 44px; height: 44px; border-radius: 11px; display: flex; align-items: center; justify-content: center; font-size: 21px; flex-shrink: 0; }
  .lp-dropdown-label { font-size: 14px; font-weight: 600; color: #0f172a; margin-bottom: 2px; }
  .lp-dropdown-desc { font-size: 12px; color: #64748b; line-height: 1.5; }

  /* Ticker */
  .lp-ticker { overflow: hidden; white-space: nowrap; }
  .lp-ticker-inner { display: inline-flex; animation: ticker 32s linear infinite; }

  /* Stats */
  .lp-stats { display: grid; grid-template-columns: repeat(4,1fr); gap: 1px; background: #e2e8f0; border: 1px solid #e2e8f0; border-radius: 20px; overflow: hidden; }
  .lp-stat { background: #fff; padding: 32px 24px; text-align: center; }

  /* Features grid */
  .lp-features { display: grid; grid-template-columns: repeat(3,1fr); gap: 20px; }

  /* Steps */
  .lp-steps { display: grid; grid-template-columns: repeat(3,1fr); gap: 40px; }

  /* Testimonials */
  .lp-testimonials { display: grid; grid-template-columns: repeat(3,1fr); gap: 20px; }

  /* Pricing */
  .lp-pricing-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 20px; max-width: 980px; margin: 0 auto; }

  /* Footer */
  .lp-footer-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 48px; }

  /* ── MOBILE ── */
  @media (max-width: 768px) {
    .lp-nav-links, .lp-nav-signin { display: none !important; }
    .lp-hamburger { display: flex !important; }
    .lp-hero-h1 { font-size: 36px !important; letter-spacing: -1px !important; }
    .lp-hero-sub { font-size: 16px !important; }
    .lp-hero-btns { flex-direction: column !important; gap: 10px !important; }
    .lp-hero-btns a { width: 100% !important; text-align: center !important; }
    .lp-stats { grid-template-columns: repeat(2,1fr) !important; }
    .lp-features { grid-template-columns: 1fr !important; }
    .lp-steps { grid-template-columns: 1fr !important; gap: 24px !important; }
    .lp-testimonials { grid-template-columns: 1fr !important; }
    .lp-pricing-grid { grid-template-columns: 1fr !important; }
    .lp-footer-grid { grid-template-columns: 1fr 1fr !important; gap: 32px !important; }
    .lp-section { padding: 60px 20px !important; }
    .lp-section-h2 { font-size: 28px !important; }
    .lp-video-grid { grid-template-columns: 1fr !important; }
    /* Platform tiles: drop the diamond, wrap as flat grid */
    .lp-plat-row { flex-wrap: wrap !important; gap: 8px !important; margin: 0 !important; margin-bottom: 8px !important; }
    .lp-plat-tile { width: 54px !important; height: 54px !important; transform: none !important; border-radius: 12px !important; }
    .lp-plat-tile:hover { transform: scale(1.08) !important; }
    .lp-plat-icon { transform: none !important; font-size: 22px !important; }
    .lp-final-cta { padding: 48px 24px !important; }
    .lp-final-h2 { font-size: 28px !important; }
    .lp-hide-mobile { display: none !important; }
    .lp-ticker-inner { animation-duration: 20s !important; }
  }
  @media (max-width: 480px) {
    .lp-hero-h1 { font-size: 30px !important; }
    .lp-stats { grid-template-columns: 1fr 1fr !important; }
    .lp-footer-grid { grid-template-columns: 1fr !important; }
    .lp-stat { padding: 20px 16px !important; }
  }
`;

const LOGO = ({ dark = false }) => (
  <a href="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 9 }}>
    <div style={{
      width: 34, height: 34, borderRadius: 10,
      background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 16, color: '#fff', fontWeight: 800,
    }}>⊛</div>
    <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: 17, color: dark ? '#fff' : '#0f172a', letterSpacing: '-0.4px' }}>ClickSaver</span>
  </a>
);

const FEATURES = [
  { icon: '🛡️', title: 'Real-Time Fraud Detection', desc: 'Every click is analyzed in milliseconds. Bots, click farms, competitors, and VPNs are caught before they waste your budget.', color: '#2563eb', bg: '#eff6ff' },
  { icon: '🚫', title: 'Automatic IP Blocking', desc: 'Fraudulent IPs are pushed directly to your Google Ads exclusion list automatically — no manual work required.', color: '#7c3aed', bg: '#f5f3ff' },
  { icon: '🚨', title: 'Fraud Spike Alerts', desc: 'Get an instant email or Slack ping the moment a campaign\'s fraud rate crosses your threshold. Catch budget-draining attacks before they do real damage.', color: '#dc2626', bg: '#fef2f2', badge: 'New' },
  { icon: '📊', title: 'Deep Fraud Analytics', desc: 'Understand exactly where fraud comes from — by device, city, ISP, time of day — with actionable insights.', color: '#059669', bg: '#f0fdf4' },
  { icon: '🔗', title: 'Google Ads Integration', desc: 'Connects directly to your Google Ads account and applies IP exclusions to all your campaigns in real-time.', color: '#0891b2', bg: '#ecfeff' },
  { icon: '⚡', title: '5-Minute Setup', desc: 'Add one lightweight script to your site and you\'re protected. Works with WordPress, Shopify, Webflow, and any HTML site.', color: '#d97706', bg: '#fffbeb' },
  { icon: '📈', title: 'ROI Savings Dashboard', desc: 'See exactly how much you\'re saving every month. Our customers average 15–25% savings on their Google Ads spend.', color: '#0ea5e9', bg: '#f0f9ff' },
  { icon: '📧', title: 'Weekly & Monthly Reports', desc: 'White-label PDF/HTML reports sent automatically to your inbox — perfect for client reporting and agency work.', color: '#8b5cf6', bg: '#f5f3ff' },
];

const STEPS = [
  { num: 1, icon: '🚀', title: 'Create a Campaign', desc: 'Connect your domain and set up your first campaign in under 2 minutes. No technical knowledge needed.' },
  { num: 2, icon: '📋', title: 'Install the Script', desc: 'Copy and paste one lightweight JavaScript snippet onto your website. Works with any CMS or custom site.' },
  { num: 3, icon: '🛡️', title: 'Fraud Blocked Automatically', desc: 'ClickSaver monitors every click 24/7 and pushes fraudulent IPs to your Google Ads exclusions in real-time.' },
];

const TESTIMONIALS = [
  { quote: 'ClickSaver saved us over $3,200 in the first month. We were losing money to competitors and bots — now our campaigns are spotless.', name: 'Marcus T.', role: 'Performance Marketing Manager', initials: 'MT', color: '#2563eb' },
  { quote: 'Setup took under 5 minutes. Within 24 hours we saw fraudulent IPs being blocked in our Google Ads account. Absolute game changer.', name: 'Sarah K.', role: 'Digital Marketing Director', initials: 'SK', color: '#7c3aed' },
  { quote: 'We manage 40+ client accounts. ClickSaver makes it easy to monitor fraud across all campaigns at once. Clients love the savings.', name: 'James R.', role: 'PPC Agency Owner', initials: 'JR', color: '#059669' },
];

const PLANS = [
  { name: 'Starter', price: '$69', period: '/mo', features: ['10,000 clicks/month', 'Real-time detection', 'Automatic IP blocking', 'Google Ads sync', 'Email reports', 'Email support'], highlighted: false },
  { name: 'Pro', price: '$109', period: '/mo', features: ['50,000 clicks/month', 'Everything in Starter', 'Advanced analytics', 'Competitor detection', 'VPN & proxy detection', 'Priority support'], highlighted: true, badge: 'Most Popular' },
  { name: 'Advanced', price: '$139', period: '/mo', features: ['Unlimited clicks', 'Everything in Pro', 'Custom detection rules', 'Multi-campaign view', 'White-glove onboarding', 'Dedicated support'], highlighted: false },
];

const FRAUD_TYPES = ['🤖 Bot Traffic', '⚔️ Competitor Clicks', '🏭 Click Farms', '🔒 VPN & Proxy Fraud', '🔁 Repeat Clickers', '🕷️ Scrapers', '👆 Accidental Clicks', '⛔ Invalid Traffic (IVT)'];

const YOUTUBE_CHANNEL = 'https://www.youtube.com/@ClickSaver';
const YOUTUBE_VIDEOS = [
  { id: 'P3CVMLn6Z5k', title: 'How to use ClickSaver — complete Google Ads click-fraud setup guide' },
  { id: 'YLw0SxRYAIA', title: 'Why click-fraud protection matters for Google Ads' },
  { id: 'x_IlqlRuajI', title: 'How to prove click-fraud savings to your agency clients' },
];

const PRODUCTS = [
  { to: '/product/total-protection', icon: '◉', bg: '#eff6ff', label: 'Total Protection Suite', desc: 'All-in-one shield across every paid and organic channel.' },
  { to: '/product/ppc-shield',       icon: '⛉', bg: '#f0fdf4', label: 'PPC Shield',             desc: 'Real-time click-fraud blocking for Google Ads, Meta & Microsoft.' },
  { to: '/product/bot-mitigation',   icon: '◍', bg: '#fef3c7', label: 'Bot Mitigation',         desc: 'Stop scrapers, crawlers and automation beyond the click.' },
  { to: '/product/lead-shield',      icon: '⤶', bg: '#fdf2f8', label: 'Lead Shield',            desc: 'Score every form submission and block fake leads before they hit your CRM.' },
];

const RESOURCES = [
  { to: '/about',            icon: 'ℹ️', bg: '#f0f9ff', label: 'About Us',         desc: 'Our mission and how ClickSaver works.' },
  { to: '/blog',             icon: '📝', bg: '#eff6ff', label: 'Blog',             desc: 'Articles and guides on click fraud.' },
  { to: '/case-studies',     icon: '📊', bg: '#f0fdf4', label: 'Case Studies',     desc: 'Real results from ClickSaver customers.' },
  { to: '/faq',              icon: '💬', bg: '#f5f3ff', label: 'FAQ',              desc: 'Answers to common questions.' },
  { to: '/knowledge-center', icon: '📚', bg: '#fff7ed', label: 'Knowledge Center', desc: 'Setup guides, docs, and tutorials.' },
  { to: '/press',            icon: '📰', bg: '#fef2f2', label: 'Press',            desc: 'ClickSaver in the news and media.' },
  { to: '/research',         icon: '🔬', bg: '#ecfeff', label: 'Research',         desc: 'Data and insights on ad fraud trends.' },
  { to: '/reviews',          icon: '⭐', bg: '#fffbeb', label: 'Reviews',          desc: 'What customers say about ClickSaver.' },
  { to: '/why-us',           icon: '🏆', bg: '#f0fdf4', label: 'Why Us?',          desc: 'How we compare to alternatives.' },
];

const FooterLink = ({ to, children }) => (
  <Link to={to} style={{ display: 'block', marginBottom: 10, color: '#64748b', textDecoration: 'none', fontSize: 14, transition: 'color .2s' }}
    onMouseEnter={e => e.target.style.color = '#0f172a'}
    onMouseLeave={e => e.target.style.color = '#64748b'}>{children}</Link>
);
const FooterA = ({ href, children }) => (
  <a href={href} style={{ display: 'block', marginBottom: 10, color: '#64748b', textDecoration: 'none', fontSize: 14, transition: 'color .2s' }}
    onMouseEnter={e => e.target.style.color = '#0f172a'}
    onMouseLeave={e => e.target.style.color = '#64748b'}>{children}</a>
);

export default function Landing() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const productRef = useRef(null);
  const resourcesRef = useRef(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    const fn = (e) => {
      if (productRef.current && !productRef.current.contains(e.target)) setProductOpen(false);
      if (resourcesRef.current && !resourcesRef.current.contains(e.target)) setResourcesOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  return (
    <div className="lp-root">
      <style>{CSS}</style>

      {/* ── Mobile menu overlay ── */}
      <div className={`lp-mobile-menu ${menuOpen ? 'open' : ''}`}>
        <button onClick={() => setMenuOpen(false)} style={{ position: 'absolute', top: 18, right: 18, background: '#f1f5f9', border: 'none', borderRadius: 8, width: 36, height: 36, fontSize: 20, cursor: 'pointer', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        <a href="#features" className="lp-mobile-a" onClick={() => setMenuOpen(false)}>Features</a>
        <a href="#how-it-works" className="lp-mobile-a" onClick={() => setMenuOpen(false)}>How It Works</a>
        <Link to="/pricing" className="lp-mobile-a" onClick={() => setMenuOpen(false)}>Pricing</Link>
        <Link to="/agencies" className="lp-mobile-a" onClick={() => setMenuOpen(false)}>Agencies</Link>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '1px', textTransform: 'uppercase', padding: '16px 0 8px' }}>Resources</div>
        {RESOURCES.map(r => (
          <Link key={r.to} to={r.to} className="lp-mobile-a" onClick={() => setMenuOpen(false)}>
            <span>{r.icon}</span> {r.label}
          </Link>
        ))}
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Link to="/login" className="lp-btn lp-btn-outline" onClick={() => setMenuOpen(false)} style={{ width: '100%', fontSize: 15 }}>Sign In</Link>
          <Link to="/register" className="lp-btn lp-btn-blue" onClick={() => setMenuOpen(false)} style={{ width: '100%', fontSize: 15, padding: '13px 20px' }}>Start Free Trial</Link>
        </div>
      </div>

      {/* ── Navbar ── */}
      <nav className={`lp-nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="lp-nav-inner">
          <LOGO />
          <div className="lp-nav-links">
            <div
              className="lp-dropdown"
              ref={productRef}
              onMouseEnter={() => setProductOpen(true)}
              onMouseLeave={() => setProductOpen(false)}
            >
              <button className={`lp-dropdown-btn ${productOpen ? 'open' : ''}`} onClick={() => setProductOpen(o => !o)}>
                Product <span className={`lp-dropdown-arrow ${productOpen ? 'open' : ''}`}>▾</span>
              </button>
              {productOpen && (
                <div className="lp-dropdown-menu">
                  {PRODUCTS.map((p) => (
                    <Link key={p.label} to={p.to} className="lp-dropdown-item" onClick={() => setProductOpen(false)}>
                      <div className="lp-dropdown-icon" style={{ background: p.bg }}>{p.icon}</div>
                      <div>
                        <div className="lp-dropdown-label">{p.label}</div>
                        <div className="lp-dropdown-desc">{p.desc}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link to="/pricing" className="lp-nav-a">Pricing</Link>
            <Link to="/agencies" className="lp-nav-a">Agencies</Link>
            <div
              className="lp-dropdown"
              ref={resourcesRef}
              onMouseEnter={() => setResourcesOpen(true)}
              onMouseLeave={() => setResourcesOpen(false)}
            >
              <button className={`lp-dropdown-btn ${resourcesOpen ? 'open' : ''}`} onClick={() => setResourcesOpen(o => !o)}>
                Resources <span className={`lp-dropdown-arrow ${resourcesOpen ? 'open' : ''}`}>▾</span>
              </button>
              {resourcesOpen && (
                <div className="lp-dropdown-menu">
                  {RESOURCES.map((r) => (
                    <Link key={r.to} to={r.to} className="lp-dropdown-item" onClick={() => setResourcesOpen(false)}>
                      <div className="lp-dropdown-icon" style={{ background: r.bg }}>{r.icon}</div>
                      <div>
                        <div className="lp-dropdown-label">{r.label}</div>
                        <div className="lp-dropdown-desc">{r.desc}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="lp-nav-right">
            <Link to="/login" className="lp-nav-a lp-nav-signin">Sign In</Link>
            <Link to="/register" className="lp-btn lp-btn-blue" style={{ padding: '9px 18px' }}>Start Free Trial</Link>
            <button className="lp-hamburger" onClick={() => setMenuOpen(true)} aria-label="Open menu">
              <span /><span /><span />
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section style={{ paddingTop: 64, background: 'linear-gradient(180deg, #f8faff 0%, #ffffff 100%)', position: 'relative', overflow: 'hidden' }}>
        {/* Decorative blobs */}
        <div style={{ position: 'absolute', top: -100, right: -100, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(37,99,235,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -80, left: -80, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,58,237,0.05) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '96px 24px 80px', position: 'relative', zIndex: 1 }}>
          {/* Badge */}
          <div className="lp-fadeup" style={{ marginBottom: 24 }}>
            <span className="lp-label">
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', display: 'inline-block', animation: 'pulse 2s ease infinite' }} />
              Protecting $2M+ in ad spend every month
            </span>
          </div>

          <h1 className="lp-hero-h1 lp-fadeup lp-delay1" style={{ fontSize: 60, fontWeight: 900, lineHeight: 1.08, letterSpacing: '-2.5px', maxWidth: 780, marginBottom: 24 }}>
            Stop Paying for{' '}
            <span className="lp-gradient-text">Fake Clicks</span>{' '}
            on Your Google Ads
          </h1>

          <p className="lp-hero-sub lp-fadeup lp-delay2" style={{ fontSize: 19, color: '#475569', lineHeight: 1.7, maxWidth: 540, marginBottom: 40 }}>
            ClickSaver detects and blocks bots, competitors, and invalid traffic in real-time — saving you 15–25% of your Google Ads spend automatically.
          </p>

          <div className="lp-hero-btns lp-fadeup lp-delay3" style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 48 }}>
            <Link to="/register" className="lp-btn lp-btn-blue-lg">Start Free Trial →</Link>
            <a href="#how-it-works" className="lp-btn lp-btn-outline">See How It Works</a>
          </div>

          <div className="lp-fadeup" style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {['No credit card required', '5-minute setup', 'Cancel anytime'].map(t => (
              <span key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                <span style={{ color: '#22c55e', fontWeight: 700 }}>✓</span>{t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Fraud type ticker ── */}
      <div style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', padding: '14px 0', overflow: 'hidden' }}>
        <div className="lp-ticker">
          <div className="lp-ticker-inner">
            {[...FRAUD_TYPES, ...FRAUD_TYPES].map((f, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 28px', fontSize: 13, color: '#475569', fontWeight: 500, whiteSpace: 'nowrap' }}>
                {f}<span style={{ color: '#cbd5e1', marginLeft: 14 }}>·</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Stats ── */}
      <section className="lp-section" style={{ padding: '80px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div className="lp-stats">
            {[
              { value: '$84B', label: 'Lost to click fraud yearly', sub: 'worldwide', color: '#dc2626' },
              { value: '22%', label: 'Of ad spend wasted', sub: 'on average per advertiser', color: '#d97706' },
              { value: '15–25%', label: 'Average savings', sub: 'for ClickSaver customers', color: '#059669' },
              { value: '<1s', label: 'Detection speed', sub: 'real-time protection', color: '#2563eb' },
            ].map((s, i) => (
              <div key={i} className="lp-stat">
                <div style={{ fontSize: 38, fontWeight: 900, color: s.color, fontFamily: 'Inter, sans-serif', letterSpacing: '-1.5px', marginBottom: 6 }}>{s.value}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 3 }}>{s.label}</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="lp-section" style={{ padding: '100px 24px', background: '#f8fafc' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 60 }}>
            <div className="lp-label" style={{ marginBottom: 16 }}>Features</div>
            <h2 className="lp-section-h2" style={{ fontSize: 40, fontWeight: 800, letterSpacing: '-1.5px', marginBottom: 14, lineHeight: 1.15 }}>
              Everything you need to protect your ad spend
            </h2>
            <p style={{ fontSize: 17, color: '#64748b', maxWidth: 500, margin: '0 auto' }}>
              Built specifically for Google Ads advertisers serious about eliminating wasted spend.
            </p>
          </div>
          <div className="lp-features">
            {FEATURES.map((f, i) => (
              <div key={i} className="lp-card" style={{ padding: '28px 24px', position: 'relative' }}>
                {f.badge && (
                  <span style={{ position: 'absolute', top: 16, right: 16, background: '#dc2626', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', padding: '3px 8px', borderRadius: 999 }}>{f.badge}</span>
                )}
                <div style={{ width: 48, height: 48, borderRadius: 13, background: f.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, marginBottom: 16 }}>{f.icon}</div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 10, lineHeight: 1.4 }}>{f.title}</h3>
                <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="lp-section" style={{ padding: '100px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <div className="lp-label" style={{ background: '#f0fdf4', color: '#059669', borderColor: '#bbf7d0', marginBottom: 16 }}>How It Works</div>
            <h2 className="lp-section-h2" style={{ fontSize: 40, fontWeight: 800, letterSpacing: '-1.5px', marginBottom: 14 }}>Up and running in 5 minutes</h2>
            <p style={{ fontSize: 17, color: '#64748b', maxWidth: 440, margin: '0 auto' }}>No developer needed. No complex integrations. Just copy, paste, and protect.</p>
          </div>
          <div className="lp-steps">
            {STEPS.map((s, i) => (
              <div key={i} style={{ textAlign: 'center', padding: '0 16px' }}>
                <div style={{ position: 'relative', display: 'inline-block', marginBottom: 20 }}>
                  <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#f8fafc', border: '2px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30 }}>{s.icon}</div>
                  <div style={{ position: 'absolute', top: -6, right: -6, width: 26, height: 26, borderRadius: '50%', background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 3px #fff' }}>{s.num}</div>
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>{s.title}</h3>
                <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Platform Compatibility ── */}
      <section style={{ padding: '80px 24px', background: '#fff', overflow: 'hidden' }}>
        <div style={{ maxWidth: 860, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', marginBottom: 10 }}>ClickSaver works with any platform</h2>
          <p style={{ fontSize: 15, color: '#64748b', marginBottom: 52, lineHeight: 1.7, maxWidth: 460, margin: '0 auto 52px' }}>
            Whether you run a custom site or use a popular CMS, ClickSaver takes just minutes to install.
          </p>
          {/* Diamond grid */}
          <div style={{ position: 'relative' }}>
            {[
              /* Row 1 */
              [
                { label: 'WordPress',   Icon: SiWordpress,       bg: '#21759b' },
                { label: 'Shopify',     Icon: SiShopify,         bg: '#95bf47' },
                { label: 'Webflow',     Icon: SiWebflow,         bg: '#4353ff' },
                { label: 'Wix',         Icon: SiWix,             bg: '#1a1a1a' },
                { label: 'Squarespace', Icon: SiSquarespace,     bg: '#222222' },
                { label: 'Joomla',      Icon: SiJoomla,          bg: '#f44321' },
              ],
              /* Row 2 */
              [
                { label: 'Drupal',      Icon: SiDrupal,          bg: '#0077c0' },
                { label: 'WooCommerce', Icon: SiWoocommerce,     bg: '#7f54b3' },
                { label: 'BigCommerce', Icon: SiBigcommerce,     bg: '#34313f' },
                { label: 'Tumblr',      Icon: SiTumblr,          bg: '#35465c' },
                { label: 'Blogger',     Icon: SiBlogger,         bg: '#fb8f3d' },
                { label: 'Google Tag',  Icon: SiGoogletagmanager,bg: '#4285f4' },
              ],
              /* Row 3 */
              [
                { label: 'Framer',      Icon: SiFramer,          bg: '#0055ff' },
                { label: 'Elementor',   Icon: SiElementor,       bg: '#92003b' },
                { label: 'Ghost',       Icon: SiGhost,           bg: '#15171a' },
                { label: 'HubSpot',     Icon: SiHubspot,         bg: '#ff7a59' },
                { label: 'Custom HTML', Icon: FaCode,            bg: '#059669' },
              ],
            ].map((row, ri) => (
              <div key={ri} className="lp-plat-row" style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: ri < 2 ? -18 : 0, marginLeft: ri % 2 !== 0 ? 52 : 0 }}>
                {row.map((p, pi) => (
                  <div key={pi} title={p.label} className="lp-plat-tile" style={{
                    width: 72, height: 72, flexShrink: 0,
                    background: p.bg,
                    borderRadius: 16,
                    transform: 'rotate(45deg)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.14)',
                    transition: 'transform .25s, box-shadow .25s',
                    cursor: 'default',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'rotate(45deg) scale(1.12)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'rotate(45deg)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.14)'; }}
                  >
                    <p.Icon className="lp-plat-icon" style={{ transform: 'rotate(-45deg)', color: '#fff', fontSize: 26 }} />
                  </div>
                ))}
              </div>
            ))}
            {/* Fade edges on mobile */}
          </div>
          <p style={{ marginTop: 44, fontSize: 13, color: '#94a3b8' }}>…and any other platform that supports a JavaScript snippet</p>
        </div>
      </section>

      {/* ── Video showcase ── */}
      <section className="lp-section" style={{ padding: '56px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div className="lp-label" style={{ background: '#fef2f2', color: '#dc2626', borderColor: '#fecaca', marginBottom: 12 }}>Watch on YouTube</div>
            <h2 className="lp-section-h2" style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-1px', marginBottom: 8 }}>See ClickSaver in action</h2>
            <p style={{ fontSize: 14, color: '#475569', maxWidth: 560, margin: '0 auto', lineHeight: 1.55 }}>
              Short, no-fluff walkthroughs: setup, detection, dashboard tour, and real fraud caught in the wild.
            </p>
          </div>

          {YOUTUBE_VIDEOS.length > 0 ? (
            <div style={{ maxWidth: 1040, margin: '0 auto', display: 'grid', gridTemplateColumns: '1.7fr 1fr', gap: 24, alignItems: 'start' }} className="lp-video-grid">
              {/* Hero video */}
              <div>
                <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 16, overflow: 'hidden', background: '#000', boxShadow: '0 24px 60px -24px rgba(15,23,42,0.28), 0 2px 6px rgba(15,23,42,0.06)' }}>
                  <iframe
                    src={`https://www.youtube.com/embed/${YOUTUBE_VIDEOS[0].id}?rel=0&modestbranding=1`}
                    title={YOUTUBE_VIDEOS[0].title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                  />
                </div>
                <div style={{ marginTop: 14, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ width: 3, alignSelf: 'stretch', background: '#2563eb', borderRadius: 2, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', lineHeight: 1.4 }}>{YOUTUBE_VIDEOS[0].title}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>Featured walkthrough · @ClickSaver</div>
                  </div>
                </div>
              </div>

              {/* Side playlist */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10, paddingLeft: 2 }}>Up next</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {YOUTUBE_VIDEOS.slice(1, 3).map((v, i) => (
                    <a key={i} href={`https://www.youtube.com/watch?v=${v.id}`} target="_blank" rel="noopener noreferrer"
                      style={{ textDecoration: 'none', color: 'inherit', display: 'flex', gap: 12, padding: 8, borderRadius: 12, transition: 'background .15s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{ width: 128, aspectRatio: '16 / 9', flexShrink: 0, position: 'relative', borderRadius: 8, overflow: 'hidden', background: `#000 url(https://img.youtube.com/vi/${v.id}/mqdefault.jpg) center / cover` }}>
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.25) 100%)' }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.95)', color: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, paddingLeft: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>▶</div>
                        </div>
                      </div>
                      <div style={{ padding: '2px 0', minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{v.title}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>@ClickSaver</div>
                      </div>
                    </a>
                  ))}
                </div>
                <a href={YOUTUBE_CHANNEL} target="_blank" rel="noopener noreferrer"
                  style={{ marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 2px', fontSize: 13, fontWeight: 500, color: '#2563eb', textDecoration: 'none' }}
                  onMouseEnter={e => { e.currentTarget.style.gap = '10px'; }}
                  onMouseLeave={e => { e.currentTarget.style.gap = '6px'; }}
                >
                  View full channel on YouTube →
                </a>
              </div>
            </div>
          ) : (
            // Channel CTA when no specific videos are listed yet
            <a
              href={YOUTUBE_CHANNEL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'flex', maxWidth: 900, margin: '0 auto', alignItems: 'center', gap: 28, padding: '36px 40px', background: 'linear-gradient(135deg,#fff5f5,#fff)', border: '1px solid #fecaca', borderRadius: 20, textDecoration: 'none', color: 'inherit', transition: 'transform .15s, box-shadow .15s', flexWrap: 'wrap' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 24px 60px -20px rgba(220,38,38,0.25)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ width: 84, height: 84, borderRadius: 20, background: '#dc2626', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="48" height="34" viewBox="0 0 24 17" fill="currentColor"><path d="M23.5 2.6a3 3 0 0 0-2.1-2.1C19.5 0 12 0 12 0S4.5 0 2.6.5A3 3 0 0 0 .5 2.6C0 4.5 0 8.5 0 8.5s0 4 .5 5.9a3 3 0 0 0 2.1 2.1C4.5 17 12 17 12 17s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1c.5-1.9.5-5.9.5-5.9s0-4-.5-5.9z"/><path d="M9.5 12.1V4.9l6.3 3.6-6.3 3.6z" fill="#dc2626"/></svg>
              </div>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Watch our full video library</div>
                <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.6 }}>Setup walkthroughs, feature deep-dives, and real fraud caught in the wild — all on the @ClickSaver YouTube channel.</div>
              </div>
              <div className="lp-btn lp-btn-blue" style={{ padding: '12px 24px', fontSize: 15 }}>Visit channel →</div>
            </a>
          )}
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="lp-section" style={{ padding: '100px 24px', background: '#f8fafc' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 60 }}>
            <div className="lp-label" style={{ background: '#f5f3ff', color: '#7c3aed', borderColor: '#ddd6fe', marginBottom: 16 }}>Customer Stories</div>
            <h2 className="lp-section-h2" style={{ fontSize: 40, fontWeight: 800, letterSpacing: '-1.5px' }}>Advertisers love ClickSaver</h2>
          </div>
          <div className="lp-testimonials">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="lp-card" style={{ padding: '28px 24px' }}>
                <div style={{ display: 'flex', gap: 2, marginBottom: 16 }}>
                  {[...Array(5)].map((_, j) => <span key={j} style={{ color: '#f59e0b', fontSize: 15 }}>★</span>)}
                </div>
                <p style={{ fontSize: 15, color: '#334155', lineHeight: 1.7, marginBottom: 24, fontStyle: 'italic' }}>"{t.quote}"</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700 }}>{t.initials}</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Customer Success Stories ── */}
      <section style={{ padding: '80px 24px 100px', background: '#fafaf9' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 34, fontWeight: 900, color: '#0f172a', letterSpacing: '-1px', marginBottom: 10 }}>Customer success stories</h2>
            <p style={{ fontSize: 15, color: '#64748b', maxWidth: 480, margin: '0 auto', lineHeight: 1.7 }}>
              Case studies showing how ClickSaver customers increased conversion, achieved lower CPC, and blocked fake clicks.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
            {[
              {
                bg: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
                emoji: '🏢',
                tag: 'E-commerce',
                headline: '$65,000+ worth of fraudulent clicks prevented',
                bullets: ['31% cleaner traffic', '+9,500 clicks saved'],
                link: '/case-studies',
              },
              {
                bg: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                emoji: '🚀',
                tag: 'SaaS',
                headline: '20% CPA improvement, lead cost decreased by 40%',
                bullets: ['47% cleaner traffic', '+54,100 saved'],
                link: '/case-studies',
              },
              {
                bg: 'linear-gradient(135deg, #f8f4f0 0%, #e8ddd4 100%)',
                emoji: '📋',
                tag: 'Agency',
                headline: '$500,000+ savings by preventing click fraud',
                bullets: ['CPC -22% achieved', '+148,903 clicks saved'],
                link: '/case-studies',
                dark: false,
              },
            ].map((s, i) => (
              <div key={i} style={{ borderRadius: 20, overflow: 'hidden', border: '1.5px solid #e2e8f0', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column' }}>
                {/* Image area */}
                <div style={{ height: 180, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                  <span style={{ fontSize: 64 }}>{s.emoji}</span>
                  <span style={{ position: 'absolute', top: 14, left: 14, fontSize: 11, fontWeight: 700, background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', color: s.dark === false ? '#475569' : '#fff', padding: '4px 12px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.25)' }}>{s.tag}</span>
                </div>
                {/* Content */}
                <div style={{ padding: '22px 24px 24px', background: '#fff', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <h3 style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', lineHeight: 1.4, marginBottom: 14 }}>{s.headline}</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
                    {s.bullets.map((b, j) => (
                      <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: '#22c55e', fontWeight: 700, fontSize: 13 }}>✓</span>
                        <span style={{ fontSize: 13, color: '#475569' }}>{b}</span>
                      </div>
                    ))}
                  </div>
                  <a href={s.link} style={{ marginTop: 'auto', fontSize: 13, color: '#2563eb', fontWeight: 700, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    View case study →
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── GDPR & CCPA Banner ── */}
      <section style={{ background: '#fff', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 32 }}>
          <div style={{ flex: '1 1 320px' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 8, letterSpacing: '-0.5px' }}>GDPR & CCPA Compliant</div>
            <p style={{ fontSize: 15, color: '#64748b', lineHeight: 1.7, maxWidth: 400 }}>ClickSaver is committed to protecting your data and respecting your privacy. We never sell your data or your visitors' data.</p>
          </div>
          <div style={{ display: 'flex', gap: 28, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* GDPR Badge */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <div style={{ position: 'relative', width: 72, height: 72 }}>
                <div style={{ width: 72, height: 72, borderRadius: '50%', border: '2px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                  <span style={{ fontSize: 26 }}>🔒</span>
                  {/* Stars around circle */}
                  {[0,60,120,180,240,300].map((deg, i) => (
                    <span key={i} style={{ position: 'absolute', fontSize: 9, color: '#fbbf24', transform: `rotate(${deg}deg) translateY(-34px) rotate(-${deg}deg)` }}>★</span>
                  ))}
                </div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '0.5px' }}>GDPR</span>
            </div>
            <div style={{ width: 1, height: 56, background: '#e2e8f0' }} />
            {/* CCPA Badge */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 72, height: 72, borderRadius: '50%', border: '2px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
                <span style={{ fontSize: 26 }}>🐻</span>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '0.5px' }}>CCPA</span>
            </div>
            <div style={{ width: 1, height: 56, background: '#e2e8f0' }} />
            {/* SSL Badge */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 72, height: 72, borderRadius: '50%', border: '2px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
                <span style={{ fontSize: 26 }}>🛡️</span>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '0.5px' }}>SSL Secured</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Awards & Recognition Banner ── */}
      <section style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flex: '1 1 280px' }}>
            {/* Leader Badge */}
            <div style={{ flexShrink: 0, width: 80, height: 80, borderRadius: 14, background: 'linear-gradient(135deg, #f97316, #ea580c)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 8, boxShadow: '0 4px 16px rgba(249,115,22,0.3)' }}>
              <span style={{ fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 2 }}>Leader</span>
              <span style={{ fontSize: 20, color: '#fff' }}>🏆</span>
              <span style={{ fontSize: 8, fontWeight: 700, color: 'rgba(255,255,255,0.8)', marginTop: 2, textAlign: 'center', lineHeight: 1.2 }}>Spring 2026</span>
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', lineHeight: 1.3 }}>The leading click fraud<br />protection software</div>
            </div>
          </div>
          {/* Award badges */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            {[
              { label: 'Happiest Users', sub: '2026', bg: 'linear-gradient(135deg, #22c55e, #16a34a)', icon: '😊' },
              { label: 'Customers Love Us', sub: '2026', bg: 'linear-gradient(135deg, #f97316, #ea580c)', icon: '❤️' },
              { label: 'Quality Choice', sub: '2026', bg: 'linear-gradient(135deg, #2563eb, #1d4ed8)', icon: '⭐' },
            ].map((b, i) => (
              <div key={i} style={{ width: 88, height: 88, borderRadius: '50%', background: b.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(0,0,0,0.15)', flexShrink: 0 }}>
                <span style={{ fontSize: 20, marginBottom: 2 }}>{b.icon}</span>
                <span style={{ fontSize: 8, fontWeight: 800, color: '#fff', textAlign: 'center', lineHeight: 1.2, padding: '0 6px' }}>{b.label}</span>
                <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.8)', marginTop: 1 }}>{b.sub}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Social proof band ── */}
      <section style={{ background: '#fafaf9', borderBottom: '1px solid #e2e8f0', padding: '56px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <div style={{ fontSize: 34, fontWeight: 900, color: '#0f172a', letterSpacing: '-1px', lineHeight: 1.25, marginBottom: 24 }}>
            Over <span style={{ color: '#2563eb' }}>500,000+</span> campaigns<br className="lp-hide-mobile" /> worldwide are protected by ClickSaver
          </div>
          <Link to="/register" style={{ display: 'inline-block', background: '#2563eb', color: '#fff', textDecoration: 'none', padding: '14px 36px', borderRadius: 12, fontSize: 16, fontWeight: 700, boxShadow: '0 4px 16px rgba(37,99,235,0.35)', transition: 'all .2s' }}>Start free trial</Link>
        </div>
      </section>

      {/* ── Pricing Preview ── */}
      <section className="lp-section" style={{ padding: '100px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 60 }}>
            <div className="lp-label" style={{ background: '#fffbeb', color: '#d97706', borderColor: '#fde68a', marginBottom: 16 }}>Pricing</div>
            <h2 className="lp-section-h2" style={{ fontSize: 40, fontWeight: 800, letterSpacing: '-1.5px', marginBottom: 14 }}>Simple, transparent pricing</h2>
            <p style={{ fontSize: 17, color: '#64748b' }}>No hidden fees. No contracts. Cancel anytime.</p>
          </div>
          <div className="lp-pricing-grid">
            {PLANS.map((p, i) => (
              <div key={i} style={{
                padding: '32px 24px', borderRadius: 18, position: 'relative',
                background: p.highlighted ? '#2563eb' : '#fff',
                border: p.highlighted ? '2px solid #2563eb' : '1.5px solid #e2e8f0',
                boxShadow: p.highlighted ? '0 20px 64px rgba(37,99,235,0.25)' : '0 1px 3px rgba(0,0,0,0.06)',
              }}>
                {p.badge && (
                  <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: '#f59e0b', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 16px', borderRadius: 20, whiteSpace: 'nowrap' }}>{p.badge}</div>
                )}
                <div style={{ fontSize: 15, fontWeight: 700, color: p.highlighted ? 'rgba(255,255,255,0.8)' : '#475569', marginBottom: 8 }}>{p.name}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, marginBottom: 24 }}>
                  <span style={{ fontSize: 44, fontWeight: 900, color: p.highlighted ? '#fff' : '#0f172a', letterSpacing: '-2px' }}>{p.price}</span>
                  <span style={{ fontSize: 14, color: p.highlighted ? 'rgba(255,255,255,0.6)' : '#94a3b8' }}>{p.period}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
                  {p.features.map((f, j) => (
                    <div key={j} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                      <span style={{ color: p.highlighted ? 'rgba(255,255,255,0.7)' : '#22c55e', fontWeight: 700, flexShrink: 0 }}>✓</span>
                      <span style={{ color: p.highlighted ? 'rgba(255,255,255,0.9)' : '#475569' }}>{f}</span>
                    </div>
                  ))}
                </div>
                <Link to="/register" style={{
                  display: 'block', textAlign: 'center', padding: '11px 0', borderRadius: 10,
                  fontSize: 14, fontWeight: 600, textDecoration: 'none',
                  background: p.highlighted ? '#fff' : '#2563eb',
                  color: p.highlighted ? '#2563eb' : '#fff',
                  boxShadow: p.highlighted ? '0 2px 8px rgba(0,0,0,0.12)' : '0 2px 8px rgba(37,99,235,0.3)',
                  transition: 'all .2s',
                }}>Get Started</Link>
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', marginTop: 24 }}>
            <Link to="/pricing" style={{ color: '#2563eb', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>View full feature comparison →</Link>
          </p>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="lp-section" style={{ padding: '80px 24px', background: '#f8fafc' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div className="lp-final-cta" style={{ background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', borderRadius: 24, padding: '72px 56px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: -60, left: -40, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
            <h2 className="lp-final-h2" style={{ fontSize: 38, fontWeight: 900, color: '#fff', letterSpacing: '-1.5px', marginBottom: 16, lineHeight: 1.2, position: 'relative' }}>
              Ready to stop losing money<br />to click fraud?
            </h2>
            <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.8)', marginBottom: 36, lineHeight: 1.6, position: 'relative' }}>
              Join hundreds of advertisers protecting their Google Ads budget. Setup takes 5 minutes.
            </p>
            <Link to="/register" style={{ display: 'inline-block', background: '#fff', color: '#2563eb', textDecoration: 'none', padding: '15px 36px', borderRadius: 12, fontSize: 16, fontWeight: 700, boxShadow: '0 8px 24px rgba(0,0,0,0.2)', transition: 'all .2s', position: 'relative' }}>
              Start Free Trial — No Card Required
            </Link>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginTop: 18, position: 'relative' }}>Cancel anytime · 5-minute setup · No credit card</p>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{ background: '#0f172a', padding: '64px 24px 32px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="lp-footer-grid" style={{ marginBottom: 48 }}>
            <div>
              <LOGO dark />
              <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.7, marginTop: 14, maxWidth: 260 }}>
                Real-time click fraud protection for Google Ads advertisers. Stop paying for fake clicks.
              </p>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 16 }}>Product</div>
              <FooterLink to="/#features">Features</FooterLink>
              <FooterLink to="/#how-it-works">How It Works</FooterLink>
              <FooterLink to="/pricing">Pricing</FooterLink>
              <FooterLink to="/agencies">Agencies</FooterLink>
              <FooterLink to="/login">Dashboard</FooterLink>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 16 }}>Company</div>
              <FooterLink to="/about">About Us</FooterLink>
              <FooterLink to="/blog">Blog</FooterLink>
              <FooterLink to="/press">Press</FooterLink>
              <FooterLink to="/contact">Contact</FooterLink>
              <FooterLink to="/support">Support</FooterLink>
              <a href={YOUTUBE_CHANNEL} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, color: '#64748b', textDecoration: 'none', fontSize: 14, transition: 'color .2s' }}
                onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                onMouseLeave={e => e.currentTarget.style.color = '#64748b'}>
                <svg width="18" height="12" viewBox="0 0 24 17" fill="#dc2626"><path d="M23.5 2.6a3 3 0 0 0-2.1-2.1C19.5 0 12 0 12 0S4.5 0 2.6.5A3 3 0 0 0 .5 2.6C0 4.5 0 8.5 0 8.5s0 4 .5 5.9a3 3 0 0 0 2.1 2.1C4.5 17 12 17 12 17s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1c.5-1.9.5-5.9.5-5.9s0-4-.5-5.9z"/><path d="M9.5 12.1V4.9l6.3 3.6-6.3 3.6z" fill="#fff"/></svg>
                YouTube
              </a>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 16 }}>Legal</div>
              <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
              <FooterLink to="/terms-policy">Terms of Service</FooterLink>
              <FooterLink to="/cookie-policy">Cookie Policy</FooterLink>
              <FooterLink to="/gdpr">GDPR</FooterLink>
            </div>
          </div>
          <div style={{ borderTop: '1px solid #1e293b', paddingTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <span style={{ fontSize: 13, color: '#334155' }}>© {new Date().getFullYear()} ClickSaver. All rights reserved.</span>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#334155' }}>🔒 Secured by Stripe</span>
              <span style={{ fontSize: 12, color: '#334155' }}>·</span>
              <span style={{ fontSize: 12, color: '#334155' }}>GDPR Compliant</span>
            </div>
          </div>
        </div>
      </footer>
      <AIChatWidget />
    </div>
  );
}
